import { createReportConfig } from "@/services/api/reports";
import type { Report } from "@/services/api/report-builder/types";
import type { ReportConfig } from "@/services/api/reports/types";
import type { NotificationContextType } from "@/context/NotificationContext";
import { generatePDFFilename } from "@/utils/formatters";
import { defaultAsAtDateForFilename } from "@/utils/reportAsAtDate";
import { buildReportConfigData } from "@/utils/reportConfigData";
import { shouldSplitMultiResourceDownload } from "@/routes/reportsGenerateHelpers";
import { runAsyncPdfDownload } from "@/utils/pdfGenerationFlow";
import type { PdfGenerationUiState } from "@/utils/pdfGenerationStages";

/** Resource row for report configs (portfolio, benchmark, slot binding, etc.). */
export interface PortfolioResource {
  id: string;
  type: string;
  slot_id?: string;
  name?: string;
}

export type SingleGenerateExportType = "view" | "view-static" | "download";

/**
 * Picks stable config id from createReportConfig response (API may use config_id, id, or legacy _id).
 */
export function resolveCreatedReportConfigId(
  cfg: ReportConfig & { _id?: string },
): string {
  for (const candidate of [cfg.config_id, cfg.id, cfg._id]) {
    if (candidate != null && String(candidate).trim() !== "") {
      return String(candidate).trim();
    }
  }
  console.error("[reports] createReportConfig returned no config id", cfg);
  throw new Error("Missing report config id from server");
}

export type SingleGenerateNotificationDeps = Pick<
  NotificationContextType,
  | "showProgress"
  | "updateNotification"
  | "removeNotification"
  | "showSuccess"
  | "showError"
  | "showWarning"
>;

/**
 * Creates one report config and opens preview or downloads PDF (used by /reports single-generate).
 */
export function openReportViewUrl(
  url: string,
  _openInOs?: (url: string) => boolean,
): void {
  // View Report / Slideshow always open in a real new browser tab so the
  // user can use the browser's native print dialog. Inside the OS shell
  // these views are too cramped to print from comfortably.
  window.open(url, "_blank", "noopener,noreferrer");
}

export async function generateSingleReportPDF(
  report: Report,
  resources: PortfolioResource[],
  asAt?: string,
  exportType: SingleGenerateExportType = "download",
  filename?: string,
  openInOs?: (url: string) => boolean,
  onProgress?: (state: PdfGenerationUiState) => void,
): Promise<void> {
  const cfg = await createReportConfig({
    name: `Config for ${report.name || "Report"} - ${new Date().toLocaleString()}`,
    data: buildReportConfigData({
      reportId: report._id,
      resources,
      asAtDate: asAt,
    }),
  });
  const configId = resolveCreatedReportConfigId(
    cfg as ReportConfig & { _id?: string },
  );

  const isLandscape = (report as { data?: { orientation?: string } })?.data
    ?.orientation === "landscape";

  if (exportType === "view") {
    let url = `/report?template=report-builder&config=${configId}`;
    if (isLandscape) url += "&landscape=true";
    url += "&presentation=true&review=true";
    openReportViewUrl(url, openInOs);
  } else if (exportType === "view-static") {
    let url = `/pdf?config=${configId}&template=report-builder`;
    if (isLandscape) url += "&landscape=true";
    openReportViewUrl(url, openInOs);
  } else {
    const fileName =
      filename ||
      generatePDFFilename(
        report,
        asAt || defaultAsAtDateForFilename(),
        resources.length === 1 ? resources[0]?.name || resources[0]?.id : null,
      );
    const pdfId = fileName.replace(/\.pdf$/i, "") || "report";

    await runAsyncPdfDownload({
      pdfRequest: {
        id: pdfId,
        template: "report-builder",
        config: configId,
        landscape: isLandscape ? "true" : "false",
        export: "pdf",
        filename: fileName.replace(/\.pdf$/i, ""),
      },
      onProgress,
    });
  }
}

export interface PerformReportsSingleGenerateDeps {
  notification: SingleGenerateNotificationDeps;
  setIsPDFExporting: (v: boolean) => void;
  setPdfGenerationProgress?: (state: PdfGenerationUiState | null) => void;
  closeExportDialog: () => void;
  openInOs?: (url: string) => boolean;
}

/**
 * /reports single-generate: one or many PDFs from one template, matching ExportDialog callbacks.
 */
export async function performReportsSingleGenerate(
  report: Report,
  resources: PortfolioResource[],
  asAt: string | undefined,
  exportType: SingleGenerateExportType,
  filename: string | undefined,
  deps: PerformReportsSingleGenerateDeps,
): Promise<void> {
  const { notification, setIsPDFExporting, setPdfGenerationProgress, closeExportDialog, openInOs } = deps;
  let progressId: string | null = null;

  if (exportType === "download") {
    setIsPDFExporting(true);

    if (shouldSplitMultiResourceDownload(report, resources.length, exportType)) {
      progressId = notification.showProgress(
        `Generating ${resources.length} Reports`,
        0,
        {
          message: "Starting report generation...",
          autoClose: false,
        },
      );

      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  if (shouldSplitMultiResourceDownload(report, resources.length, exportType)) {
    let successCount = 0;
    let failureCount = 0;

    try {
      for (let i = 0; i < resources.length; i++) {
        const resource = resources[i];

        if (progressId) {
          const startProgress = Math.round((i / resources.length) * 100);
          notification.updateNotification(progressId, {
            progress: startProgress,
            message: `Generating report ${i + 1} of ${resources.length}...`,
          });
        }

        try {
          const resourceName = resource?.name || resource?.id;
          const resourceFilename = generatePDFFilename(
            report,
            asAt || defaultAsAtDateForFilename(),
            resourceName,
          );
          await generateSingleReportPDF(
            report,
            [resource],
            asAt,
            "download",
            resourceFilename,
            openInOs,
            setPdfGenerationProgress,
          );
          successCount++;

          if (progressId) {
            const progress = Math.round(((i + 1) / resources.length) * 100);
            notification.updateNotification(progressId, {
              progress,
              message: `Report ${i + 1} of ${resources.length} completed`,
            });
          }
        } catch (reportError) {
          console.error(`Failed to generate report ${i + 1}:`, reportError);
          failureCount++;
          if (progressId) {
            notification.updateNotification(progressId, {
              progress: Math.round(((i + 1) / resources.length) * 100),
              message: `Report ${i + 1} failed, continuing...`,
            });
          }
        }

        if (i < resources.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      if (progressId) {
        notification.removeNotification(progressId);

        if (failureCount === 0) {
          notification.showSuccess(
            "Reports Generated Successfully",
            `${successCount} reports have been generated and downloaded individually.`,
            {
              duration: 8000,
            },
          );
        } else if (successCount === 0) {
          notification.showError(
            "Report Generation Failed",
            `All ${failureCount} reports failed to generate. Please try again.`,
            { duration: 8000 },
          );
        } else {
          notification.showWarning(
            "Partial Report Generation",
            `${successCount} reports succeeded, ${failureCount} reports failed. Please check and retry failed reports.`,
            { duration: 10000 },
          );
        }
      }
    } catch (error) {
      console.error("Batch report generation failed:", error);
      if (progressId) {
        notification.removeNotification(progressId);
        notification.showError(
          "Report Generation Failed",
          "There was an error generating the reports. Please try again.",
          { duration: 8000 },
        );
      }
    } finally {
      setIsPDFExporting(false);
      closeExportDialog();
    }
    return;
  }

  try {
    await generateSingleReportPDF(
      report,
      resources,
      asAt,
      exportType,
      filename,
      openInOs,
      setPdfGenerationProgress,
    );
  } catch (error) {
    console.error(`Failed to ${exportType} PDF:`, error);
    throw error;
  } finally {
    if (exportType === "download") {
      setIsPDFExporting(false);
    } else {
      closeExportDialog();
    }
  }
}
